import {useActiveRoute} from '../../../hooks/use-active-route'
import {useFeatureState} from '../../../hooks/use-feature-state'
import {FeatureState} from '../../../page-data/payloads/header'
import {OverviewFeedback} from './OverviewFeedback'
import {Button} from '@primer/react'
import {AlertIcon} from '@primer/octicons-react'
import {useState} from 'react'
import {announce} from '@github-ui/aria-live'
import {useToggleFeaturePreview} from '../../../hooks/use-toggle-feature-preview'
import {useLayoutRouteData} from '../../../page-data/loaders/use-layout-route-data'
import {usePullRequestAnalytics} from '../../../hooks/use-pull-request-analytics'

const ENABLE_ERROR_MESSAGE = 'Overview preview could not be turned on. Try again.'

export function OverviewFeatureToggle() {
  const {urls} = useLayoutRouteData()
  const toggleFeature = useToggleFeaturePreview('prs_overview_react')
  const renderState = useOverviewToggleRenderState()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const [enabling, setEnabling] = useState(false)
  const [enableError, setEnableError] = useState(false)

  const handleToggleOn = async () => {
    if (enabling) return

    setEnabling(true)
    setEnableError(false)

    try {
      await toggleFeature({enabled: true})
    } catch {
      setEnabling(false)
      setEnableError(true)
      announce(ENABLE_ERROR_MESSAGE, {assertive: true})
      return
    }

    sendPullRequestAnalyticsEvent('overview.feature_toggle', 'OVERVIEW_FEATURE_TOGGLE', {enabled: true})

    window.location.href = `${urls.conversation}?overview=true`
  }

  if (renderState === 'feedback') {
    return <OverviewFeedback />
  }

  if (renderState === 'toggle') {
    return (
      <>
        {/* Kept out of the button label so PageHeader.Actions (min-width: max-content) can't overflow narrow viewports. */}
        {enableError && (
          <span id="overview-enable-error" className="sr-only">
            {ENABLE_ERROR_MESSAGE}
          </span>
        )}
        <Button
          onClick={handleToggleOn}
          loading={enabling}
          variant="invisible"
          size="small"
          leadingVisual={enableError ? AlertIcon : undefined}
          aria-label={enableError ? 'Try the Overview page, try again' : 'Try the Overview page'}
          aria-describedby={enableError ? 'overview-enable-error' : undefined}
        >
          {enableError ? (
            'Try again'
          ) : (
            <>
              <span aria-hidden="true">✨ </span>
              Try the Overview page
            </>
          )}
        </Button>
      </>
    )
  }

  return null
}

// This hook is a bit redundant with the logic in PullRequestHeader, but it allows us to keep the rendering logic
// for the overview feature preview in one place and avoid potential bugs where the header and the toggle get out of sync.
export function useOverviewToggleRenderState() {
  const overviewFeatureState = useFeatureState('overview')
  const activeRoute = useActiveRoute()

  if (overviewFeatureState === FeatureState.Enabled && activeRoute === 'overview') {
    return 'feedback'
  }

  if (overviewFeatureState === FeatureState.Available && activeRoute === 'conversation') {
    return 'toggle'
  }

  return 'none'
}
